import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Clipboard, Upload, X, Link2, Loader2 } from 'lucide-react';

/**
 * Paste an image from clipboard, drag-drop a file, or pick from disk.
 * The selected image is downscaled + JPEG-compressed client-side and stored
 * as a data URL string in the parent form. A plain URL text input remains
 * available for cases where the operator already has a hosted image URL.
 */
const MAX_DIM = 900;   // px; longest side after downscale
const QUALITY = 0.82;  // JPEG quality
const MAX_BYTES = 700 * 1024; // 700 KB cap for the resulting data URL

const readableSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const compressToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Failed to read file'));
  reader.onload = () => {
    const img = new Image();
    img.onerror = () => reject(new Error('Not a valid image'));
    img.onload = () => {
      const ratio = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff'; // flatten transparency to avoid PNG bloat
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      // Try progressively lower quality until under cap
      let q = QUALITY;
      let dataUrl = canvas.toDataURL('image/jpeg', q);
      while (dataUrl.length > MAX_BYTES * 1.37 && q > 0.4) { // 1.37 ≈ base64 overhead
        q -= 0.1;
        dataUrl = canvas.toDataURL('image/jpeg', q);
      }
      resolve({ dataUrl, width: w, height: h });
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const ImagePasteField = ({ value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState(null); // {width, height, size}
  const [mode, setMode] = useState(() => {
    if (!value) return 'paste';
    return value.startsWith('data:') ? 'paste' : 'url';
  });
  const zoneRef = useRef(null);
  const fileInputRef = useRef(null);

  const processFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('That does not look like an image.');
      return;
    }
    setBusy(true); setError('');
    try {
      const { dataUrl, width, height } = await compressToDataUrl(file);
      onChange(dataUrl);
      setMeta({ width, height, size: dataUrl.length });
      setMode('paste');
    } catch (e) {
      setError(e.message || 'Failed to process image');
    } finally { setBusy(false); }
  }, [onChange]);

  // Paste anywhere while the drop-zone is focused / hovered
  useEffect(() => {
    const node = zoneRef.current;
    if (!node) return;
    const handler = async (e) => {
      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type && it.type.startsWith('image/')) {
          e.preventDefault();
          await processFile(it.getAsFile());
          return;
        }
      }
    };
    node.addEventListener('paste', handler);
    return () => node.removeEventListener('paste', handler);
  }, [processFile]);

  const onDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) await processFile(file);
  };

  const clear = () => { onChange(''); setMeta(null); setError(''); };

  const isDataUrl = value && value.startsWith('data:');

  return (
    <div>
      {/* Toggle */}
      <div className="flex gap-1 mb-2">
        <button
          type="button"
          data-testid="image-mode-paste"
          onClick={() => setMode('paste')}
          className={`inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-md border transition-colors ${mode === 'paste' ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white dark:bg-[#0f0f10] border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:border-neutral-400'}`}
        >
          <Clipboard size={12} /> Paste / Upload
        </button>
        <button
          type="button"
          data-testid="image-mode-url"
          onClick={() => setMode('url')}
          className={`inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-md border transition-colors ${mode === 'url' ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white dark:bg-[#0f0f10] border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:border-neutral-400'}`}
        >
          <Link2 size={12} /> URL
        </button>
      </div>

      {mode === 'url' ? (
        <input
          data-testid="image-url-input"
          className="w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2.5 text-[14px] outline-none focus:border-neutral-400 dark:focus:border-neutral-600"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…/product.jpg"
        />
      ) : (
        <div
          ref={zoneRef}
          data-testid="image-paste-zone"
          tabIndex={0}
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
          className="relative border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg p-5 text-center cursor-pointer bg-neutral-50/50 dark:bg-neutral-900/40 hover:border-neutral-400 dark:hover:border-neutral-500 focus:outline-none focus:border-neutral-500 transition-colors"
        >
          <input
            ref={fileInputRef}
            data-testid="image-file-input"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => processFile(e.target.files?.[0])}
          />
          {busy ? (
            <div className="inline-flex items-center gap-2 text-[13px] text-neutral-600 dark:text-neutral-300">
              <Loader2 size={14} className="animate-spin" /> Processing image…
            </div>
          ) : (
            <>
              <Upload size={22} className="mx-auto text-neutral-400 mb-2" />
              <div className="text-[13px] text-neutral-700 dark:text-neutral-200">
                <span className="font-medium">Paste (Ctrl/⌘ + V)</span>, drop, or click to upload
              </div>
              <div className="text-[11.5px] text-neutral-500 mt-1">
                PNG or JPG · auto-compressed to ~700 KB · max {MAX_DIM}px longest side
              </div>
            </>
          )}
        </div>
      )}

      {error && <p className="text-red-600 text-[12.5px] mt-2" data-testid="image-error">{error}</p>}

      {value && (
        <div className="mt-3 flex items-start gap-3">
          <div className="w-24 h-24 rounded-md border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center overflow-hidden shrink-0">
            <img
              data-testid="image-preview"
              src={value}
              alt="preview"
              className="max-h-full max-w-full object-contain"
              onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
            />
          </div>
          <div className="flex-1 min-w-0 text-[11.5px] text-neutral-500">
            {isDataUrl ? (
              <>
                <div>Embedded image ({readableSize((value.length * 3) / 4)})</div>
                {meta && <div>Resized to {meta.width}×{meta.height}</div>}
              </>
            ) : (
              <div className="truncate">{value}</div>
            )}
            <button
              type="button"
              data-testid="image-clear-btn"
              onClick={clear}
              className="mt-1 inline-flex items-center gap-1 text-[11.5px] text-neutral-600 hover:text-red-500"
            >
              <X size={11} /> Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagePasteField;
